import { trpc, queryClient } from '@/utils/trpc';
import { useMutation, useQuery } from '@tanstack/react-query';
import { PaginationState } from '@/components/Pagination';

interface ListUsersParams {
  pagination?: PaginationState;
  organizationProfileIds?: string[];
  excludeOwners?: boolean;
}

export const useListUsers = ({
  pagination,
  organizationProfileIds,
  excludeOwners,
}: ListUsersParams = {}) =>
  useQuery(
    trpc.usersRouter.getOrganizationUsers.queryOptions({
      pagination: pagination
        ? {
            offset: pagination.page * pagination.pageSize,
            limit: pagination.pageSize,
          }
        : undefined,
      organizationProfileIds,
      excludeOwners,
    }),
  );

export const useDeactivateUser = () =>
  useMutation({
    ...trpc.usersRouter.deactivateUser.mutationOptions(),
    onSuccess: (_, variables) => {
      // Always invalidate the users list to refresh the table user status
      queryClient.invalidateQueries({
        queryKey: trpc.usersRouter.getOrganizationUsers.queryKey(),
      });

      // Always invalidate the specific user query user status
      queryClient.invalidateQueries({
        queryKey: trpc.usersRouter.getUser.queryKey({
          userId: variables.userId,
        }),
      });
    },
  });

export const useGetUserById = (userId: string) =>
  useQuery(trpc.usersRouter.getUser.queryOptions({ userId }));

export const useUpdateUser = () => {
  return useMutation({
    ...trpc.usersRouter.updateUser.mutationOptions(),
    onSuccess: (_, variables) => {
      // Always invalidate the users list to refresh the table
      queryClient.invalidateQueries({
        queryKey: trpc.usersRouter.getOrganizationUsers.queryKey(),
      });

      // Always invalidate the specific user query
      queryClient.invalidateQueries({
        queryKey: trpc.usersRouter.getUser.queryKey({
          userId: variables.userId,
        }),
      });

      // Only invalidate current user profile if updating own profile
      const currentUserData = queryClient.getQueryData(
        trpc.userSettingsRouter.getCurrentUser.queryKey(),
      );

      if (currentUserData && currentUserData.id === variables.userId) {
        queryClient.invalidateQueries({
          queryKey: trpc.userSettingsRouter.getCurrentUser.queryKey(),
        });
      }
    },
  });
};

export const useCreateUser = () =>
  useMutation({
    ...trpc.usersRouter.inviteUser.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: trpc.usersRouter.getOrganizationUsers.queryKey(),
      });
    },
  });

export const useGetProviderTypes = (options?: { enabled?: boolean }) =>
  useQuery({
    ...trpc.metadataRouter.getProviderTypes.queryOptions(),
    select: (data) =>
      data?.map((provider) => ({
        value: provider.id,
        label: provider.displayName,
        disabled: false,
      })) || [],
    staleTime: 5 * 60 * 1000,
    ...options,
  });

export const useGetCurrentUserProfileAccess = (options?: {
  enabled?: boolean;
}) =>
  useQuery({
    ...trpc.userSettingsRouter.getOrganizationProfiles.queryOptions(),
    select: (data) =>
      data?.data?.map((profile) => ({
        value: profile.id,
        label: profile.displayName,
        avatarType: profile.avatarType,
        disabled: false,
      })) || [],
    staleTime: 5 * 60 * 1000,
    ...options,
  });

export const useUserReinvite = () =>
  useMutation({
    ...trpc.usersRouter.resendUserInvitation.mutationOptions(),
    onSuccess: () => {
      //invalidate the users list to refresh invitation status
      queryClient.invalidateQueries({
        queryKey: trpc.usersRouter.getOrganizationUsers.queryKey(),
      });
    },
  });
